<template>
    <v-sheet
        v-ripple="ripple"
        rounded
        :elevation="elevation"
        :outlined="outlined"
        :width="width"
        :class="classList"
        @click="$emit('click')"
    >
        <div class="app-card__top">
            <div v-if="findSlot('before')" class="app-card__before">
                <slot name="before" />
            </div>

            <header class="app-card__header">
                <div class="app-card__header-content">
                    <div v-if="findSlot('overline')" class="app-card__overline overline">
                        <slot name="overline" />
                    </div>
                    <div v-if="findSlot('title')" class="app-card__title primaryText--text">
                        <slot name="title" />
                    </div>
                    <div v-if="findSlot('subtitle')" class="app-card__subtitle text--secondary">
                        <slot name="subtitle" />
                    </div>
                    <div v-if="findSlot('header')">
                        <slot name="header" />
                    </div>
                </div>
            </header>
            <aside v-if="findSlot('actions')" class="app-card__actions">
                <slot name="actions" />
            </aside>
        </div>
        <app-scrollbar v-if="findSlot('default')" class="app-card__body">
            <main>
                <slot />
            </main>
        </app-scrollbar>
        <footer v-if="findSlot('footer')" class="app-card__footer">
            <slot name="footer" />
        </footer>
        <div v-if="findSlot('after')" class="app-card__after">
            <slot name="after" />
        </div>
    </v-sheet>
</template>

<script>
export default {
    name: 'AppCard',
    props: {
        loading: {
            type: Boolean,
            default: false
        },
        ripple: {
            type: Boolean,
            default: false
        },
        size: {
            type: String,
            default: 'medium' // x-small, small, medium, large, x-large, fullscreen
        },
        elevation: {
            type: Number,
            default: 6
        },
        outlined: {
            type: Boolean,
            default: false
        },
        actionsRight: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        fullscreen() {
            return this.size === 'fullscreen';
        },
        classList() {
            let classList = ['app-card'];

            if (this.findSlot('default')) {
                classList.push('app-card--with-body');
            }

            if (this.actionsRight) {
                classList.push('app-card--with-actions-right');
            }

            if (this.dense) {
                classList.push('app-card--dense');
            }

            return classList.join(' ');
        },
        width() {
            let width = '';
            let sizeValue = this.size || 'medium';

            switch (sizeValue) {
                case 'x-small':
                    width = '200';
                    break;
                case 'small':
                    width = '310';
                    break;
                case 'medium':
                    width = '410';
                    break;
                case 'large':
                    width = '570';
                    break;
                case 'x-large':
                    width = '1000';
                    break;
                case 'xx-large':
                    width = '80%';
                    break;
                case 'fullscreen':
                    width = '100%';
                    break;
                case 'auto':
                    width = 'auto';
                    break;
                default:
                    width = sizeValue;
                    break;
            }

            return width;
        }
    }
};
</script>

<style lang="scss" scoped>
$xPadding: 1rem;
$yPadding: 1rem;

.app-card {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-width: thin;
    max-width: 100%;
    max-height: 100%;
    outline: none;
    text-decoration: none;
    transition-property: box-shadow, opacity;
    position: relative;
    white-space: normal;

    &--with-body {
        .app-card__header {
            padding-bottom: $yPadding/2;
            border-bottom: 1px solid var(--v-background-darken3);
        }
    }

    &--with-actions-right {
        .app-card__top {
            flex-direction: row;
            justify-content: space-between;
        }
    }

    &__top {
        display: flex;
        flex-direction: column;
    }

    &__header {
        padding: $yPadding $xPadding $yPadding/2 $xPadding;
        flex: 1;
    }

    &__actions {
        flex: 0 0 auto;
    }

    &__body {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        flex-grow: 1;
        font-size: 0.875rem;
        main {
            padding: $yPadding/2 $xPadding;
        }
    }

    &__footer {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-end;
        background: var(--v-primaryLight-base);
        padding: $yPadding $xPadding;
    }

    &__overline {
        font-size: 0.75rem;
    }

    &__title {
        display: block;
        font-size: 1.5rem;
        line-height: 1.4;
        font-weight: normal;
    }

    &__subtitle {
        font-size: 0.875rem;
    }

    &--dense {
        .app-card__title {
            font-size: 1.2rem;
        }
        .app-card__overline {
            font-size: 0.7rem !important;
        }
    }
}
</style>
